/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useAppSelector } from "src/store";
import { useCampaignConfigService } from '../../campaignConfigBuilder/services/CampaignConfigService';
import { useCampaignConfigLoader } from '../../campaignConfigBuilder/hooks/useCampaignConfigLoader';
import { setConfigCategoriesLoading, setConfigCategoriesError, setConfigCategories, addConfigCategory, updateConfigCategory as updateConfigCategoryAction, updateConfigCategoriesFilters, deleteConfigCategory } from '../slices/adminConfigCategoriesSlice';

export const useAdminConfigCategoriesService = () => {
  const dispatch = useDispatch();
  const { isLoading, filters, configCategories, totalCount, error } = useAppSelector((state) => state.adminConfigCategories);
  const campaignConfigService = useCampaignConfigService();
  const { loadCampaignConfigs } = useCampaignConfigLoader();

  const getConfigCategories = useCallback(async () => {
    try {
      dispatch(setConfigCategoriesLoading(true));
      dispatch(setConfigCategoriesError(null));
      const response = await campaignConfigService.getCampaignConfigCategories(filters);
      dispatch(setConfigCategories({ items: response.items, count: response.count }));
      return response;
    } catch (error) {
      dispatch(setConfigCategoriesError('Failed to fetch config categories'));
      throw error;
    } finally {
      dispatch(setConfigCategoriesLoading(false));
    }
  }, [dispatch, campaignConfigService, filters]);

  const createConfigCategory = useCallback(async (data: any) => {
    try {
      const response = await campaignConfigService.createCampaignConfigCategory(data);
      dispatch(addConfigCategory(response));
      // Refresh the campaign config slice to update categories in the selection dialog
      await loadCampaignConfigs(true);
      return response;
    } catch (error) {
      dispatch(setConfigCategoriesError('Failed to create config category'));
      throw error;
    }
  }, [dispatch, campaignConfigService, loadCampaignConfigs]);

  const updateConfigCategory = useCallback(async (name: string, data: any) => {
    try {
      const response = await campaignConfigService.updateCampaignConfigCategory(name, data);
      dispatch(updateConfigCategoryAction(response));
      // Refresh the campaign config slice to update categories in the selection dialog
      await loadCampaignConfigs(true);
      return response;
    } catch (error) {
      dispatch(setConfigCategoriesError('Failed to update config category'));
      throw error;
    }
  }, [dispatch, campaignConfigService, loadCampaignConfigs]);

  const deleteConfigCategories = useCallback(async (names: string[]) => {
    try {
      await campaignConfigService.deleteCampaignConfigCategories(names);
      // Remove deleted categories from the state
      names.forEach(name => dispatch(deleteConfigCategory(name)));
      // Refresh the campaign config slice to update categories in the selection dialog
      await loadCampaignConfigs(true);
    } catch (error) {
      dispatch(setConfigCategoriesError('Failed to delete config categories'));
      throw error;
    }
  }, [dispatch, campaignConfigService, loadCampaignConfigs]);

  const changeFilters = useCallback((newFilters: any) => {
    dispatch(updateConfigCategoriesFilters(newFilters));
  }, [dispatch]);

  return {
    isLoading,
    filters,
    configCategories,
    totalCount,
    error,
    getConfigCategories,
    createConfigCategory,
    updateConfigCategory,
    deleteConfigCategories,
    changeFilters,
  };
};
